import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import { ConnectedProps, connect } from 'react-redux';
import { Column, Table, Spacer, toast, withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { ReceivedErrorModal } from 'src/views/modals/received-error-modal/received-error-modal';
import { useTranslation } from 'react-i18next';
import translations from 'src/internationalisation/translation-map.json';
import { validateString } from 'src/validation/common/validate-string';
import { validateNumber } from 'src/validation/common/validate-number';
import { getCurrencies } from 'src/store/entities/company-settings/company-settings';
import i18n from 'i18next';
import {
  addPriceBookItem,
  priceBookItemCellValueUpdated,
  updatePriceBookItem,
  removePriceBookItem,
  initialPriceBookItem,
  importPriceBook,
  getPriceBookItems,
} from 'src/store/entities/price-book/price-book';
import { getSelectOptions } from 'src/common/lists/lists';
import { IPriceBookItem } from 'src/common/interfaces/price-book.interfaces';
import { debounce } from 'lodash';
import { autoSaveWait } from 'src/config/config';
import { toNum } from '@oliasoft-open-source/units';
import { nextName } from 'client/common/utils/next-name';
import { getCostCodesItems } from 'src/store/entities/cost-codes/cost-codes';
import {
  selectCostCodesIdToCodeMap,
  selectCostCodesItemNoNameOptions,
  selectCostCodesCodeToIdMap,
} from 'src/store/entities/cost-codes/selectors';
import { PriceBookType } from 'src/enums/price-book';
import { ConfirmModal } from 'src/views/modals/confirm-modal';
import type { TRootState } from 'src/store/store-types';
import {
  dataHeaders,
  downloadPriceBookExcel,
  filterAndSortDataRows,
  headings,
  typeList,
  uploadPriceBookExcel,
} from './utils';

const PriceBookTable = ({
  priceBookId,
  currenciesList,
  priceBookItems,
  isAdding,
  isUpdating,
  getCurrencies,
  addPriceBookItem,
  getPriceBookItems,
  priceBookItemCellValueUpdated,
  updatePriceBookItem,
  removePriceBookItem,
  importPriceBook,
  error,
  getCostCodesItems,
  costCodesItemsOptions,
  costCodesIdToCodeMap,
  costCodesCodeToIdMap,
}: { priceBookId: string } & PropsFromRedux) => {
  const { t } = useTranslation();
  const { company: companyId = '' } = useParams();

  const [modalVisibility, setModalVisibility] = useState(!!error);
  const [confirmState, setConfirmState] = useState<{
    visible: boolean;
    type?: 'delete' | 'upload';
    payload?: string | null;
  }>({ visible: false });

  const debounceUpdatePriceBookItem = useRef(debounce(updatePriceBookItem, autoSaveWait));

  const [filters, setFilters] = useState({});
  const [sorts, setSorts] = useState({});

  useEffect(() => {
    if (priceBookId) {
      getCurrencies();
      getPriceBookItems(priceBookId);
      getCostCodesItems();
    }
  }, [priceBookId]);

  const { dataSortCells, dataFilterCells } = dataHeaders(headings, filters, setFilters, sorts, setSorts);

  const filteredAndSortedData = filterAndSortDataRows(priceBookItems, filters, sorts, costCodesIdToCodeMap);
  useEffect(() => {
    if (error) {
      toast({
        message: {
          type: 'Error',
          heading: i18n.t(translations.priceBook_failedToImportPricebook) as string,
          content: error.message,
        },
      });

      setModalVisibility(true);
    }
  }, [error]);

  const handleCloseModal = () => {
    setModalVisibility(false);
  };
  const fieldConfigs: { [key: string]: any } = {
    name: { type: 'Input', errorFn: validateString },
    costCodesId: {
      type: 'Select',
      options: costCodesItemsOptions,
      clearable: true,
    },
    vendor: { type: 'Input', errorFn: validateString },
    price: {
      type: 'NumberInput',
      errorFn: (val: string) => validateNumber(+val),
    },
    currency: {
      type: 'Select',
      options: currenciesList,
      autoLayerWidth: true,
    },
    priceType: {
      type: 'Select',
      options: typeList,
      autoLayerWidth: true,
    },
  };

  const validateValue = (costCodesId: string | null) => {
    return !costCodesItemsOptions.some((option) => option.value === costCodesId)
      ? costCodesIdToCodeMap.get(costCodesId)
      : costCodesId;
  };

  const onConfirmModal = () => {
    if (confirmState.type === 'delete' && confirmState.payload) {
      removePriceBookItem(confirmState.payload);
    }
    if (confirmState.type === 'upload') {
      uploadPriceBookExcel(importPriceBook, priceBookId, costCodesCodeToIdMap);
    }
    setConfirmState({ visible: false, type: undefined, payload: null });
  };

  const dataRows = filteredAndSortedData.map((dataRow) => {
    const rowsCells = Object.entries(dataRow)
      .filter(
        ([key]) =>
          key !== PriceBookType.PriceBookId && key !== PriceBookType.CompanyId && key !== PriceBookType.PriceBookItemId,
      )
      .map(([key, value]) => {
        const validatedValue = key === PriceBookType.CostCodesId ? validateValue(value) : value;
        return {
          key,
          value: validatedValue,
          ...fieldConfigs[key],
          onChange: (ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
            onChangePriceBookList(dataRow, ev, key);
          },
        };
      });
    return {
      cells: rowsCells,
      actions: [
        {
          label: 'Delete',
          icon: 'minus',
          onClick: () => {
            setConfirmState({
              visible: true,
              type: 'delete',
              payload: dataRow.priceBookItemId as string,
            });
          },
        },
      ],
    };
  });

  const onAddPriceBookItem = () =>
    addPriceBookItem({
      ...initialPriceBookItem,
      name: nextName(
        priceBookItems.map(({ name }) => name),
        initialPriceBookItem.name,
      ),
      priceBookId,
      companyId,
    });

  const onChangePriceBookList = (
    priceBookItem: IPriceBookItem,
    ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    name: string,
  ) => {
    const newValue = name === PriceBookType.Price ? toNum(ev.target.value) : ev.target.value;

    priceBookItemCellValueUpdated({
      id: priceBookItem.priceBookItemId as string,
      value: newValue,
      field: name as keyof IPriceBookItem,
    });

    if (newValue !== undefined) {
      debounceUpdatePriceBookItem.current({
        ...priceBookItem,
        [name]: newValue,
      });
    }
  };

  const columnWidths = ['30%', '15%', '15%', '15%', '15%', '10%'];
  const columnAlignment = ['left', 'left', 'left', 'left', 'left', 'left'];

  const headerActions = [
    {
      icon: 'add',
      label: 'Add',
      onClick: onAddPriceBookItem,
      primary: true,
      disabled: isAdding || isUpdating,
    },
  ];

  const tableConfig = {
    testId: 'price-book-table',
    columnWidths,
    columnAlignment,
    actions: [
      {
        icon: 'download',
        tooltip: t(translations.timeTracker_download),
        onClick: () =>
          downloadPriceBookExcel({
            priceBook: priceBookItems,
            currenciesList,
            costCodesItemsOptions,
            costCodesIdToCodeMap,
          }),
      },
      {
        icon: 'upload',
        tooltip: t(translations.timeTracker_upload),
        onClick: () => {
          if (priceBookItems.length > 0) {
            setConfirmState({
              visible: true,
              type: 'upload',
              payload: null,
            });
          } else {
            uploadPriceBookExcel(importPriceBook, priceBookId, costCodesCodeToIdMap);
          }
        },
      },
    ],
    actionsRight: true,
    headers: [
      {
        actions: headerActions,
        cells: dataSortCells,
      },
      {
        cells: dataFilterCells,
      },
    ],
    rows: dataRows,
  };

  return (
    <Column spacing={0}>
      <Table table={tableConfig} />
      <Spacer />
      {modalVisibility && <ReceivedErrorModal error={error} onCloseModal={handleCloseModal} />}
      <ConfirmModal
        visible={confirmState.visible}
        setModalVisible={(visible: boolean) => setConfirmState((prev) => ({ ...prev, visible }))}
        title={confirmState.type === 'delete' ? t(translations.delete) : t(translations.upload)}
        onConfirm={onConfirmModal}
        confimLable={confirmState.type}
        confirmMessage={
          confirmState.type === 'delete'
            ? t(translations.priceBook_item_delete_confirmation)
            : t(translations.priceBook_import_confirmation)
        }
      />
    </Column>
  );
};

const mapStateToProps = (state: TRootState) => {
  const { entities } = state;
  const { currenciesList } = entities.companySettings;
  const { priceBookItems, isAdding, isUpdating, error } = entities.priceBook;
  return {
    currenciesList: getSelectOptions(currenciesList, 'code', 'code'),
    priceBookItems,
    isAdding,
    isUpdating,
    error,
    costCodesItemsOptions: selectCostCodesItemNoNameOptions(state),
    costCodesIdToCodeMap: selectCostCodesIdToCodeMap(state),
    costCodesCodeToIdMap: selectCostCodesCodeToIdMap(state),
  };
};

const mapDispatchToProps = {
  getCurrencies,
  addPriceBookItem,
  getPriceBookItems,
  priceBookItemCellValueUpdated,
  updatePriceBookItem,
  removePriceBookItem,
  importPriceBook,
  getCostCodesItems,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = withErrorBoundary(connector(PriceBookTable));

export { Container as PriceBookTable };
